/**
 * Núcleo de la trazabilidad (§10.3): cada comando se ejecuta en UNA transacción que escribe el
 * cambio, el evento de auditoría encadenado, las alertas y los correos (outbox). Si algo falla,
 * se revierte todo (AUD-001 / SYS-003). Clase sin Angular para poder probarla con IndexedDB real.
 */
import { Actor, DatosCiclo } from '../core/models/asistencia.model';
import { AlertaBorrador, AlertaEntity, AjusteCatalogoAlerta, CodigoAlerta, PlantillaCorreoEntity, PreferenciaNotificacionEntity, TipoAlertaDef } from '../core/models/alertas.model';
import { BorradorEvento, EventoAuditoria } from '../core/models/auditoria.model';
import { ErrorDominio, Evaluacion, Hallazgo, Resultado, falla, hallazgo, ok } from '../core/models/errores';
import { Capacidad, tieneCapacidad } from '../core/models/permisos';
import { AsistenciaRepository, UnidadTrabajo } from '../core/ports/asistencia.repository';
import { HASH_GENESIS, sellarEvento } from '../core/domain/auditoria';
import { alertasAResolverPorEvento, alertasPorEvento, claveDedup, materializarAlertas } from '../core/domain/motor-alertas';
import { aplicarAjustes, CATALOGO_ALERTAS } from '../core/data/catalogo-alertas';

export interface Aplicado<T> {
  evento: BorradorEvento;
  resultado: T;
  borradoresExtra?: AlertaBorrador[];
}

export interface Comando<T> {
  nombre: string;
  cicloId: string;
  capacidad?: Capacidad;
  advertenciasAceptadas?: boolean;
  evaluar?: (d: DatosCiclo, ahora: Date, actor: Actor) => Evaluacion;
  /** Evento a auditar cuando el comando queda bloqueado (RF-68). Sin él, el bloqueo no se audita. */
  eventoBloqueo?: (bloqueo: Hallazgo, d: DatosCiclo, ahora: Date) => Promise<BorradorEvento | null> | BorradorEvento | null;
  aplicar: (d: DatosCiclo, uow: UnidadTrabajo, ahora: Date, actor: Actor) => Promise<Aplicado<T>>;
}

export const CLAVE_AJUSTES_CATALOGO = 'catalogoAlertasAjustes';

export function generarId(prefijo: string): string {
  const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefijo}-${uuid}`;
}

export async function catalogoVigente(uow: UnidadTrabajo): Promise<TipoAlertaDef[]> {
  const ajustes = (await uow.kvObtener<Partial<Record<CodigoAlerta, AjusteCatalogoAlerta>>>(CLAVE_AJUSTES_CATALOGO)) ?? {};
  return aplicarAjustes(CATALOGO_ALERTAS, ajustes);
}

export function plantillasVigentes(todas: PlantillaCorreoEntity[]): Map<string, PlantillaCorreoEntity> {
  const m = new Map<string, PlantillaCorreoEntity>();
  for (const p of todas.filter(x => x.activa)) {
    const previa = m.get(p.id);
    if (!previa || p.version > previa.version) m.set(p.id, p);
  }
  return m;
}

export class EjecutorComandos {
  constructor(
    private readonly repo: AsistenciaRepository,
    private readonly ahora: () => Date,
    private readonly baseUrl: () => string
  ) {}

  /** Sella y agrega un evento a la cadena dentro de la transacción en curso. */
  async registrarEvento(uow: UnidadTrabajo, borrador: BorradorEvento, actor: Actor, ahora: Date): Promise<EventoAuditoria> {
    const ultimo = await uow.ultimoEvento();
    const evento = sellarEvento(borrador, actor, ahora, ultimo?.hash ?? HASH_GENESIS, generarId('evt'));
    let secuencia: number;
    try {
      secuencia = await uow.agregarEvento(evento);
    } catch (e) {
      console.error('[Asistencia MML] Fallo de auditoría', e);
      throw new ErrorDominio('AUD-001');
    }
    return { ...evento, secuencia };
  }

  /** Genera, deduplica y persiste alertas + correos de un evento (outbox transaccional). */
  async procesarAlertas(uow: UnidadTrabajo, evento: EventoAuditoria, datos: DatosCiclo, ahora: Date, extra: AlertaBorrador[] = []): Promise<AlertaEntity[]> {
    const abiertas = await uow.alertasAbiertas();
    const aResolver = alertasAResolverPorEvento(evento, datos, abiertas);
    if (aResolver.length) {
      const cerradas = aResolver.map(a => ({
        ...a,
        estado: 'ATENDIDA' as const,
        atendidaEn: ahora.toISOString(),
        atendidaPor: 'sistema',
        motivoCierre: `Resuelta automáticamente por ${evento.accion}`
      }));
      await uow.guardar('alertas', cerradas);
    }
    const borradores = [...alertasPorEvento(evento, datos, ahora), ...extra];
    if (!borradores.length) return [];
    return this.materializarYGuardar(uow, borradores, datos, ahora, evento.secuencia);
  }

  async materializarYGuardar(uow: UnidadTrabajo, borradores: AlertaBorrador[], datos: DatosCiclo, ahora: Date, secuencia?: number): Promise<AlertaEntity[]> {
    const claves = borradores.map(b => claveDedup(b.tipo, b.destinatario.id, b.claveEntidad));
    const [catalogo, plantillas, preferencias, dedup] = await Promise.all([
      catalogoVigente(uow),
      uow.todos('plantillas'),
      uow.todos('preferencias'),
      uow.dedupExistentes(claves)
    ]);
    const { alertas } = materializarAlertas(borradores, {
      catalogo,
      plantillas: plantillasVigentes(plantillas),
      preferencias: new Map(preferencias.map((p: PreferenciaNotificacionEntity) => [`${p.usuarioId}|${p.tipo}`, p])),
      banderas: { ...datos.ciclo.banderas, correoHabilitado: false },
      ahora,
      baseUrl: this.baseUrl(),
      cicloId: datos.ciclo.id,
      dedupExistentes: dedup,
      correosNoTransaccionalesHoy: new Map<string, number>(),
      generarId,
      origenEventoSecuencia: secuencia
    });
    await uow.guardar('alertas', alertas);
    return alertas;
  }

  async ejecutar<T>(cmd: Comando<T>, actor: Actor): Promise<Resultado<T>> {
    const ahora = this.ahora();
    try {
      return await this.repo.transaccion(async uow => {
        const datos = await uow.datosCiclo(cmd.cicloId);

        if (cmd.capacidad && !tieneCapacidad(actor.rol, cmd.capacidad)) {
          const ev = await this.registrarEvento(uow, {
            accion: 'ACCION_NO_PERMITIDA', entidad: 'SISTEMA', entidadId: cmd.nombre, resumen: `Intento de «${cmd.nombre}» sin permiso`,
            cambios: [], resultado: 'BLOQUEADO', codigoError: 'SEG-001', contexto: { cicloId: cmd.cicloId }, participantesRelacionados: []
          }, actor, ahora);
          void ev;
          return falla<T>('SEG-001');
        }

        const evaluacion = cmd.evaluar?.(datos, ahora, actor) ?? { bloqueos: [], advertencias: [] };
        if (evaluacion.bloqueos.length) {
          const bloqueo = evaluacion.bloqueos[0];
          const borrador = cmd.eventoBloqueo ? await cmd.eventoBloqueo(bloqueo, datos, ahora) : null;
          if (borrador) {
            const ev = await this.registrarEvento(uow, { ...borrador, resultado: 'BLOQUEADO', codigoError: bloqueo.codigo }, actor, ahora);
            await this.procesarAlertas(uow, ev, datos, ahora);
          }
          return { estado: 'ERROR', error: bloqueo, detalle: evaluacion.bloqueos.slice(1).map(b => b.mensaje).join(' · ') || undefined } as Resultado<T>;
        }
        if (evaluacion.advertencias.length && !cmd.advertenciasAceptadas) {
          return { estado: 'CONFIRMAR', advertencias: evaluacion.advertencias } as Resultado<T>;
        }

        const aplicado = await cmd.aplicar(datos, uow, ahora, actor);
        const evento = await this.registrarEvento(uow, {
          ...aplicado.evento,
          advertenciasAceptadas: evaluacion.advertencias.map(a => a.codigo)
        }, actor, ahora);
        await this.procesarAlertas(uow, evento, datos, ahora, aplicado.borradoresExtra);
        return ok(aplicado.resultado, evento.secuencia);
      });
    } catch (e) {
      return this.traducirError<T>(e);
    }
  }

  traducirError<T>(e: unknown): Resultado<T> {
    if (e instanceof ErrorDominio) return falla<T>(e.codigo, e.message);
    const nombre = (e as { name?: string })?.name ?? '';
    const mensaje = (e as { message?: string })?.message ?? String(e);
    console.error('[Asistencia MML] Error en comando', e);
    if (nombre === 'QuotaExceededError') return falla<T>('SYS-002', undefined, mensaje);
    if (nombre === 'ConstraintError') return falla<T>('SYS-003', 'Conflicto de unicidad: el registro ya existía. No se guardó ningún cambio.', mensaje);
    return falla<T>('SYS-003', undefined, mensaje);
  }

  hallazgoDe(codigo: Parameters<typeof hallazgo>[0]): Hallazgo {
    return hallazgo(codigo);
  }
}
